import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, Shield, ShieldAlert, Copy, Download, Eye, EyeOff } from 'lucide-react';
import { mfaAPI, verifyMFA } from '../lib/api';
import { toast } from 'sonner';

const MFASetupRequired = ({ tempToken, onSuccess, onCancel }) => {
  const [step, setStep] = useState('setup'); // setup, verify, backup
  const [setupData, setSetupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    startSetup();
  }, [tempToken]);

  const startSetup = async () => {
    try {
      // Use the temp token endpoint for MFA setup during login flow
      const response = await mfaAPI.setupWithToken(tempToken);
      setSetupData(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to initialize MFA setup. Please try logging in again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the temp token endpoint for confirmation
      const response = await mfaAPI.confirmWithToken(tempToken, code);
      setBackupCodes(response.data.backup_codes);
      setStep('backup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    // Now verify MFA to get the actual token
    setLoading(true);
    try {
      const response = await verifyMFA(tempToken, code);
      onSuccess(response.data);
    } catch (err) {
      // MFA is enabled, just complete
      onSuccess(null);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  const downloadBackupCodes = () => {
    const content = `Stock & Load - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nIMPORTANT: Store these codes in a safe place. Each code can only be used once.\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !setupData) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Setting up MFA...</p>
        </CardContent>
      </Card>
    );
  }

  if (step === 'backup') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Shield className="h-12 w-12 text-green-500" />
          </div>
          <CardTitle>Save Your Backup Codes</CardTitle>
          <CardDescription>
            Store these codes somewhere safe. Each code can only be used once.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Important:</strong> These codes will only be shown once. If you lose your authenticator app, you'll need these codes to access your account.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((backupCode, index) => (
              <code key={index} className="bg-muted px-3 py-2 rounded text-center font-mono text-sm">
                {backupCode}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={copyBackupCodes} className="flex-1">
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" onClick={downloadBackupCodes} className="flex-1">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleComplete} className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              "I've saved my codes - Continue"
            )}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <ShieldAlert className="h-12 w-12 text-yellow-500" />
        </div>
        <CardTitle>MFA Setup Required</CardTitle>
        <CardDescription>
          Your organization requires two-factor authentication. Please set it up to continue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {setupData && (
          <>
            <p className="text-sm text-center text-muted-foreground">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>

            <div className="flex justify-center bg-white p-4 rounded-lg">
              {setupData.qr_code_svg ? (
                setupData.qr_code_svg.startsWith('data:') ? (
                  <img 
                    src={setupData.qr_code_svg} 
                    alt="MFA QR Code" 
                    className="w-40 h-40"
                  />
                ) : (
                  <div 
                    dangerouslySetInnerHTML={{ __html: setupData.qr_code_svg }} 
                    className="[&>svg]:w-40 [&>svg]:h-40 [&>svg]:max-w-full"
                    style={{ width: '160px', height: '160px' }}
                  />
                )
              ) : (
                <div className="w-40 h-40 flex items-center justify-center bg-muted rounded text-sm text-muted-foreground text-center p-4">
                  QR code unavailable.<br/>Please use manual code below.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Or enter this code manually:</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-xs font-mono overflow-hidden">
                  {showSecret ? setupData.secret : '••••••••••••••••••••••••••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(setupData.secret);
                    toast.success('Secret copied');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Enter the 6-digit code from your app:</Label>
              <Input
                type="text"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest"
              />
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button 
          onClick={handleConfirm} 
          className="w-full" 
          disabled={loading || code.length !== 6}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify & Enable MFA'
          )}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} className="w-full">
            Cancel and sign out
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default MFASetupRequired;
